import { Capability } from './capabilityTypes';
import { CapabilitiesCache } from './UserCache';

export function getUserId(userUri?: string): string | null {
  if (!userUri) {
    return null;
  }

  const userIdMatch = userUri.match(/^([0-9]+)$/);
  if (userIdMatch) {
    return userIdMatch[1];
  }

  const userUriMatch = userUri.match(/users\/([0-9]+)/);
  if (userUriMatch) {
    return userUriMatch[1];
  }

  return null;
}

type Status =
  /** The capability has not been requested before. */
  | 'missing'
  /** The capability is already queued for an API request. */
  | 'queued'
  /** The capability value has been returned by an API request. */
  | 'received';
/**
 * Returns all the requestedCapabilities in an object keyed by status.
 */
export function getCapabilitiesByStatus(
  requestedCapabilities: Capability[],
  capabilitiesCache: CapabilitiesCache,
): { [K in Status]: Capability[] } {
  const byStatus = {
    missing: [] as Capability[],
    queued: [] as Capability[],
    received: [] as Capability[],
  };
  return requestedCapabilities.reduce((acc, c) => {
    switch (capabilitiesCache.currentCapabilities[c]) {
      case undefined:
        acc.missing = [...acc.missing, c];
        return acc;
      case 'queued':
        acc.queued = [...acc.queued, c];
        return acc;
      case true:
      case false:
        acc.received = [...acc.received, c];
        return acc;
    }
  }, byStatus);
}

/**
 * The return value of Object.keys is always string[], which makes the compiler
 * grumpy. So this is a utility function that calls Object.keys in a TS-friendly
 * way.
 */
export function getKeys<T>(obj: NonNullable<T>) {
  return Object.keys(obj) as Array<keyof T>;
}
